// src/features/locations/LocationOverviewCard.jsx
//
// OVERVIEW-ALIGN: Location Overview card for the 2×2 Overview grid (bottom-right).
//
// Differences from SpaceOverviewCard:
//   - Sharing ratio: staleness gate REMOVED — reads latestHeadcount.total directly.
//   - Reader-less (no utilization rows) → "No reader installed" in Peak KPI tile.
//   - data-testid="space-overview-card" kept for backward compat with existing
//     LocationDetail tests that assert the Overview tab renders this card.
//
// OFFICE-OVERVIEW-CLEANUP:
//   - AttendanceBar (Seat utilization block) removed from render.
//   - Financials row (space-overview-cost-block) removed from render.
//   - CostBasisToggle removed (no longer drives anything visible).
//
// OFFICE-OVERVIEW-CAPACITY-PANE-ENRICH:
//   - AssignedBar and TwoSegmentBar retired; replaced by RealityBars + KeyRatios.
//   - PanelHeading gains optional rightLabel (renders "As of {month} {year}").
//
// Props:
//   location        — full location object (includes installedCapacity, utilization[])
//   latestHeadcount — { total: number } | null

import React from "react";
import { T } from "../../app/tokens.js";
import { InfoTooltip } from "../../components/InfoTooltip.jsx";
import { getDisplayedSqft, getAnalyticalSqft } from "../../utils/locationSqftDerived.js";
import { SharingRatioValue } from "../../components/SharingRatioValue.jsx";
import { ReportingPeriodLabel } from "../../components/ReportingPeriodLabel.jsx";
import {
  formatReportingMonth,
  selectReportingMonthRow,
} from "../../services/reportingPeriod.js";
import { officeAttendanceCaption } from "./officeMetricVintages.js";

// ─── Pure helpers (exported for tests) ───────────────────────────────────────

export function computeSharingRatioRaw(headcountTotal, installedCapacity) {
  if (headcountTotal == null || !installedCapacity) return null;
  return headcountTotal / installedCapacity;
}

export function computeSfPerSeat(sqft, installedCapacity) {
  if (!sqft || !installedCapacity) return null;
  const result = Math.round(sqft / installedCapacity);
  // Return null (not 0) so KpiTile falls through to "—" — zero is not a
  // meaningful sf/seat value and is almost certainly missing data.
  if (result === 0) return null;
  return result;
}

/**
 * Attendance rate: avgBadges / assignedHeadcount × 100. Not clamped — cross-
 * office badge visits/day-of-week clustering can push this above 100%, and
 * the Ranking Report + Pulse trend surfaces render the same unclamped
 * definition, so the Overview card must stay consistent with them.
 * Returns integer or null. 0 is a real value; null means missing/invalid input.
 */
export function computeAttendanceRate(avgBadges, assignedHeadcount) {
  if (avgBadges == null || isNaN(avgBadges)) return null;
  if (assignedHeadcount == null || isNaN(assignedHeadcount) || assignedHeadcount <= 0) return null;
  return Math.max(0, Math.round((avgBadges / assignedHeadcount) * 100));
}

/**
 * Cost per attendee: sum of non-IT opex depts / avgBadges.
 * Returns raw number or null. Null means missing/invalid input.
 */
export function computeCostPerAttendee(opexDepts, avgBadges) {
  if (!Array.isArray(opexDepts) || opexDepts.length === 0) return null;
  if (avgBadges == null || isNaN(avgBadges) || avgBadges <= 0) return null;
  const total = opexDepts
    .filter((d) => d.department !== "it")
    .reduce((sum, d) => sum + parseFloat(d.amountUsd ?? 0), 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  return total / avgBadges;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PanelHeading({ text, rightLabel }) {
  return (
    <div
      data-testid="capacity-pane-heading"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "4px 12px",
        margin: "0 -20px",
        padding: "8px 20px",
        minHeight: 40,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{
            width: 3,
            height: 16,
            borderRadius: 2,
            background: T.blue,
            flexShrink: 0,
          }}
        />
        <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
          {text}
        </h3>
      </div>
      {rightLabel && rightLabel !== "—" && (
        <span
          data-testid="capacity-pane-as-of"
          style={{ fontSize: T.fsXs, color: T.light }}
        >
          {rightLabel}
        </span>
      )}
    </div>
  );
}


// ─── RealityBars ──────────────────────────────────────────────────────────────
//
// Three horizontal bars scaled to installed = 100%.
//   - Installed  — always 100%, label "{N} seats"
//   - Assigned   — headcount ÷ installed, label "{N} · {pct}%"
//   - Peak utilization — peakBadges ÷ installed, label "{N} · {pct}%"
// A one-line caption below surfaces the assigned-vs-peak gap.
// Renders nothing when installed is null/zero.

const BAR_TRACK_H = 8;

function BarRow({ label, caption, pct, fillColor, valueLabel, valueLabelColor, testIdSuffix }) {
  const clampedPct = Math.min(100, Math.max(0, pct));
  return (
    <div data-testid={`reality-bar-row-${testIdSuffix}`} style={{ marginBottom: 7 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 3,
          fontSize: T.fsXs,
          color: T.mid,
        }}
      >
        <span style={{ fontWeight: T.fwSemibold }}>
          {label}
          {caption && <span style={{ fontWeight: T.fwRegular, color: T.light }}> · {caption}</span>}
        </span>
        <span style={{ color: valueLabelColor ?? T.light, whiteSpace: "nowrap", marginLeft: 8 }}>{valueLabel}</span>
      </div>
      <div
        style={{
          height: BAR_TRACK_H,
          borderRadius: 4,
          background: T.border,
          overflow: "hidden",
        }}
      >
        <div
          data-testid={`reality-bar-fill-${testIdSuffix}`}
          style={{
            height: "100%",
            width: `${clampedPct}%`,
            background: fillColor,
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
}

function RealityBars({ installed, assigned, peak, awaitingLabel, attendanceCaption }) {
  if (!installed) return null;

  const assignedPct = assigned != null ? Math.round((assigned / installed) * 100) : null;
  const peakPct     = peak    != null ? Math.round((peak    / installed) * 100) : null;
  const isOverCapacity = assigned != null && installed != null && assigned > installed;

  // Caption: "99% assigned, 65% peak — 137 seats empty at peak"
  const caption = (() => {
    if (assignedPct == null || peakPct == null) return null;
    const emptyAtPeak = Math.max(0, installed - Math.round(peak));
    return `${assignedPct}% assignment of installed seats, ${peakPct}% peak utilization of installed seats - ${emptyAtPeak.toLocaleString()} seats empty at peak`;
  })();

  return (
    <div data-testid="reality-bars">
      <div
        style={{
          fontSize: T.fsXs,
          fontWeight: T.fwBold,
          letterSpacing: "0.07em",
          color: T.mid,
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        Installed vs assigned vs actually here
      </div>

      <BarRow
        label="Installed workstations"
        pct={100}
        fillColor={T.barInstalled}
        valueLabel={`${installed.toLocaleString()} seats`}
        testIdSuffix="installed"
      />
      <BarRow
        label="Assigned headcount vs installed"
        pct={assignedPct ?? 0}
        fillColor={isOverCapacity ? T.ragRed : T.blue}
        valueLabel={
          assigned != null && assignedPct != null
            ? `${assigned.toLocaleString()} · ${assignedPct}%`
            : "—"
        }
        valueLabelColor={isOverCapacity ? T.ragRed : undefined}
        testIdSuffix="assigned"
      />
      <BarRow
        label="Peak utilization"
        caption={attendanceCaption}
        pct={peakPct ?? 0}
        fillColor={T.utilGold}
        valueLabel={
          peak != null && peakPct != null
            ? `${Math.round(peak).toLocaleString()} · ${peakPct}%`
            : "—"
        }
        testIdSuffix="peak"
      />

      <div
        data-testid="reality-bars-caption"
        style={{
          fontSize: T.fsXs,
          color: T.light,
          marginTop: 2,
          visibility: !caption && !awaitingLabel ? "hidden" : undefined,
        }}
      >
        {caption ?? awaitingLabel ?? "\u00a0"}
      </div>
    </div>
  );
}

// ─── KeyRatios ────────────────────────────────────────────────────────────────
//
// Three ratio chips:
//   - Sharing ratio  — "{N}:1", footer "headcount / seat · policy 1.0:1"
//   - Build density  — "{N} sf/seat", footer "target 150–250 sf/seat"
//   - Max-density headroom — "+{N} seats", footer "{installed} of {maxCap} at max · {pct}%"
// Null inputs produce "—" rather than crashing.

function RatioChip({ label, value, footer, testId, valueColor }) {
  return (
    <div
      data-testid={testId}
      style={{
        flex: 1,
        background: T.lightGray,
        borderRadius: 8,
        padding: "10px 12px",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: T.fsXxxs5,
          fontWeight: T.fwBold,
          color: T.mid,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: T.fsMd,
          fontWeight: T.fwDisplay,
          color: valueColor ?? T.dark,
          lineHeight: 1.1,
          marginBottom: 4,
        }}
      >
        {value ?? "—"}
      </div>
      <div
        style={{
          fontSize: T.fsXxxs,
          color: T.light,
          lineHeight: 1.3,
          whiteSpace: "normal",
        }}
      >
        {footer}
      </div>
    </div>
  );
}

function KeyRatios({ sharingRatio, buildDensity, headroomN, installed, maxCap }) {
  const sharingDisplay = sharingRatio != null
    ? <SharingRatioValue numerator={sharingRatio.toFixed(1)} />
    : null;

  const densityDisplay = buildDensity != null
    ? `${buildDensity.toLocaleString()} sf/seat`
    : null;

  const headroomDisplay = headroomN != null
    ? `+${headroomN.toLocaleString()} seats`
    : null;

  const headroomFooter = (() => {
    if (installed == null || maxCap == null || maxCap === 0) {
      return "- at max density";
    }
    const pct = Math.round((installed / maxCap) * 100);
    return `${installed.toLocaleString()} of ${maxCap.toLocaleString()} at max · ${pct}%`;
  })();

  return (
    <div
      data-testid="key-ratios"
      style={{ display: "flex", gap: 8 }}
    >
      <RatioChip
        testId="key-ratio-sharing"
        label="Sharing ratio"
        value={sharingDisplay}
        footer="headcount / seat · policy 1.0:1"
      />
      <RatioChip
        testId="key-ratio-density"
        label="Build density"
        value={densityDisplay}
        footer="target 150-250 sf/seat"
      />
      <RatioChip
        testId="key-ratio-headroom"
        label="Expansion capacity"
        value={headroomDisplay}
        footer={headroomFooter}
        valueColor={T.greenDeep}
      />
    </div>
  );
}

// ─── WhiteKpiTile ─────────────────────────────────────────────────────────────
//
// White-filled primary KPI tile. Mirrors the RatioChip shell (border/radius/
// padding) but uses T.card background to sit one step above the gray derived-
// driver chips. Used only for the top-four headline metrics in this pane.
//
// Structural descriptions remain beneath their values. Only Peak Count uses
// an out-of-flow explanation to keep verbose reporting qualifiers compact.

function WhiteKpiTile({ label, value, unit, description, caption, valueTestId, valueColor, detailsInTooltip = false, tooltipTestId }) {
  const autoTestId = `kpi-value-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div
      style={{
        background: T.card,
        borderRadius: 8,
        border: `1px solid ${T.border}`,
        padding: "7px 10px",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: T.fsXxxs5,
          fontWeight: T.fwBold,
          color: T.mid,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...(detailsInTooltip && { display: "flex", alignItems: "center", gap: 4, overflow: "visible" }),
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        {/* Task #8581: Peak count keeps its verbose explanation and reporting
            caption, but out of flow so the tile stays one value tall. */}
        {detailsInTooltip && (description || caption) && (
          <InfoTooltip
            label={label}
            testId={tooltipTestId}
            text={
              <>
                {description && <span data-testid="peak-count-description" style={{ display: "block" }}>{description}</span>}
                {caption && <span data-testid="peak-count-attendance-caption" style={{ display: "block", marginTop: 3 }}>{`Monthly avg · ${caption}`}</span>}
              </>
            }
          />
        )}
      </div>
      <div
        data-testid={valueTestId ?? autoTestId}
        style={{ lineHeight: 1.1, marginBottom: 2, whiteSpace: "nowrap" }}
      >
        <span style={{ fontSize: T.fsLg5, fontWeight: T.fwDisplay, color: valueColor ?? T.dark }}>
          {value ?? "—"}
        </span>
        {unit && value != null && (
          <span style={{ fontSize: T.fsXs, color: T.mid }}> {unit}</span>
        )}
      </div>
      {description && !detailsInTooltip && (
        <div style={{ fontSize: T.fsXxxs, color: T.light, lineHeight: 1.3 }}>
          {description}
        </div>
      )}
      {caption && !detailsInTooltip && (
        <div data-testid="peak-count-attendance-caption" style={{ fontSize: T.fsXxxs, color: T.light, lineHeight: 1.3, marginTop: 3 }}>
          {`Monthly avg · ${caption}`}
        </div>
      )}
    </div>
  );
}

// ─── LocationOverviewCard ─────────────────────────────────────────────────────

export function LocationOverviewCard({ location, latestHeadcount, locationId, badgeIntegrationPending = false }) {
  const { installedCapacity, maxCapacity, displayInstalledCapacity } = location;

  // ── Dimensions ────────────────────────────────────────────────────────────────
  const sqftN = getDisplayedSqft(location, null);
  const installed =
    typeof installedCapacity === "number"
      ? installedCapacity
      : installedCapacity != null ? Number(installedCapacity) : null;
  const maxCap =
    typeof maxCapacity === "number"
      ? maxCapacity
      : maxCapacity != null ? Number(maxCapacity) : null;

  // VACATED-DETAIL-RETENTION (O-27): prefer retained pre-exit seat count for
  // the Seats KPI tile display.  installedCapacity stays as the effective
  // capacity-schedule value (0 for vacated/retired) so all derived ratio/bar
  // computations (sharing ratio, build density, headroom) are unaffected.
  const displayedSeatsN = displayInstalledCapacity != null
    ? Number(displayInstalledCapacity)
    : installed;
  const isSeatsRetained = (location.stage === "vacated" || location.stage === "retired")
    && displayInstalledCapacity != null;

  // Sharing ratio: NO staleness gate — read latestHeadcount.total directly.
  const headcountTotal  = latestHeadcount?.total ?? null;

  const sqftDisplay      = sqftN != null ? Math.round(sqftN).toLocaleString() : null;
  const installedDisplay = displayedSeatsN != null ? displayedSeatsN.toLocaleString() : null;

  // Attendance data is anchored to the canonical reporting month. A prior row
  // is used only when the canonical month is absent; attendance captions label that lag.
  const utilRows = Array.isArray(location.utilization) ? location.utilization : [];
  const peakUtil = selectReportingMonthRow(utilRows, location.reportingPeriod);
  const hasPeakData = peakUtil?.peakDailyUniqueBadges != null;
  const peakBadges = hasPeakData ? peakUtil.peakDailyUniqueBadges : null;

  // O-30: vacated/retired/closed offices have no meaningful peak attendance.
  // Suppress the peak value entirely regardless of what utilization rows contain.
  const isVacatedStage = location.stage === "vacated" || location.stage === "retired" || location.stage === "closed";

  // Effective peak: null when stage is exited.
  // O-34: badgeIntegrationPending no longer suppresses offices that have real
  // peak data — only offices with no rows (or all-null peaks) should fall back
  // to "Awaiting badge feed".  The flag is still used to drive showAwaiting.
  const effectivePeakBadges = isVacatedStage ? null : peakBadges;

  // "Awaiting badge feed" only when pending AND no real peak data exists.
  // (vacated gets "—" to signal it's not a data-quality issue.)
  const showAwaiting = !isVacatedStage && badgeIntegrationPending && !hasPeakData;

  // For the RealityBars peak bar — mirrors effectivePeakBadges suppression.
  const peakBadgesForBars = effectivePeakBadges;

  const peakSourceLabel = peakUtil?.sourceLabel
    ?? (peakUtil?.sourceSegment
      ? (String(peakUtil.sourceSegment).toLowerCase().includes("envoy") ? "Envoy" : "Alta/Verkada")
      : null);
  const attendanceCaption = isVacatedStage ? null : officeAttendanceCaption(location);
  const structuralMonth = formatReportingMonth(latestHeadcount?.snapshotDate);
  const capacityAsOfLabel = (
    <>
      <ReportingPeriodLabel
        reportingPeriod={location.reportingPeriod}
        infoTestId="capacity-pane-coverage-info"
        copyVariant="overview"
      >
        {structuralMonth ? `As of ${structuralMonth}` : "As of unknown"}
      </ReportingPeriodLabel>
      {peakSourceLabel ? ` · Source: ${peakSourceLabel}` : ""}
    </>
  );

  // ── Derived ratio values ──────────────────────────────────────────────────────
  const sharingRatio  = computeSharingRatioRaw(headcountTotal, installed);
  const buildDensity  = computeSfPerSeat(getAnalyticalSqft(location), installed);
  const headroomN     = maxCap != null && installed != null ? maxCap - installed : null;

  return (
    <div
      data-testid="space-overview-card"
      style={{
        borderRadius: 10,
        padding: "0 20px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        height: "100%",
      }}
    >
      {/* Header — "Capacity & occupancy" left, "As of {month}" right */}
      <style>{`[data-testid="peak-count-info-panel"] { left: auto !important; right: 0; }`}</style>
      <PanelHeading text="Capacity & occupancy" rightLabel={capacityAsOfLabel} />

      {/* KPI headline tiles — Sq ft · Workstations · People · Peak.
          White-filled (T.card) to read as primary facts above the gray
          derived-driver chips (OFFICE-OVERVIEW-TILES-AND-GRID).
          OFFICE-OVERVIEW-TIGHTEN: subCaptions removed; tooltips via ⓘ. */}
      <div
        data-testid="space-overview-kpi-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
          marginTop: 11,
        }}
      >
        <WhiteKpiTile
          label="Sq ft"
          value={sqftDisplay}
          unit="sf"
          description="footprint"
        />
        <WhiteKpiTile
          label="Workstations"
          value={installedDisplay}
          description={isSeatsRetained ? "pre-exit · retained" : "installed"}
          valueTestId="kpi-value-seats"
          valueColor={isSeatsRetained ? T.light : undefined}
        />
        <WhiteKpiTile
          label="People"
          value={headcountTotal != null ? headcountTotal.toLocaleString() : null}
          description="assigned"
          valueTestId="kpi-value-people"
        />
        <WhiteKpiTile
          label="Peak count"
          value={effectivePeakBadges != null ? Math.round(effectivePeakBadges).toLocaleString() : null}
          description={
            isVacatedStage
              ? "—"
              : showAwaiting
              ? "Awaiting badge feed"
              : effectivePeakBadges != null && installed && installed > 0
              ? `${Math.round((effectivePeakBadges / installed) * 100)}% peak utilization of installed seats`
              : "no reader installed"
          }
          valueTestId="kpi-value-peak"
          caption={attendanceCaption}
          detailsInTooltip
          tooltipTestId="peak-count-info"
        />
      </div>

      {/* Reality bars — installed / assigned / peak */}
      <div style={{ marginTop: 11, paddingBottom: 4 }}>
        <RealityBars
          installed={installed}
          assigned={headcountTotal}
          peak={peakBadgesForBars}
          awaitingLabel={showAwaiting ? "Awaiting badge feed" : undefined}
          attendanceCaption={attendanceCaption}
        />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: T.border, margin: "7px 0" }} />

      {/* Key ratios chips */}
      <KeyRatios
        sharingRatio={sharingRatio}
        buildDensity={buildDensity}
        headroomN={headroomN}
        installed={installed}
        maxCap={maxCap}
      />
    </div>
  );
}
